import React, { useEffect, useRef, useState, useCallback, createContext, useContext } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
    Home,
    Compass,
    PlusCircle,
    Store,
    User,
    Settings,
    LogOut,
    Crown,
    Shield,
    MessageCircle,
    Trophy,
    Stamp,
    CloudOff,
    Users,
    Medal,
    BadgeCheck,
    ChevronDown,
    Menu as MenuIcon,
    X,
    Lock,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useRealtime } from "../lib/RealtimeContext";
import { useTierGate } from "../lib/PaywallContext";
import { BRAND } from "../lib/brand";
import NotificationBell from "./NotificationBell";
import { api } from "../lib/api";

const NAV = [
    { to: "/feed", label: "Feed", icon: Home, testId: "nav-feed" },
    { to: "/explore", label: "Explore", icon: Compass, testId: "nav-explore" },
    { to: "/create", label: "Create", icon: PlusCircle, testId: "nav-create" },
    { to: "/marketplace", label: "Marketplace", icon: Store, testId: "nav-marketplace" },
    { to: "/profile", label: "Profile", icon: User, testId: "nav-profile" },
];

// Shared open-state for the mobile slide-over menu, so the bottom-bar "Menu"
// tab AND the top-bar avatar can both open the same drawer.
const MobileMenuContext = createContext(null);
export function MobileMenuProvider({ children }) {
    const [open, setOpen] = useState(false);
    return (
        <MobileMenuContext.Provider value={{ open, setOpen }}>
            {children}
        </MobileMenuContext.Provider>
    );
}
export function useMobileMenu() {
    return useContext(MobileMenuContext) || { open: false, setOpen: () => {} };
}

function MessagesNavLink({ variant = "sidebar" }) {
    const [unread, setUnread] = useState(0);
    const pollRef = useRef(null);
    const { subscribe, connected } = useRealtime();

    const fetchUnread = useCallback(async () => {
        try {
            const { data } = await api.get("/messages/unread-count");
            setUnread(data.count || 0);
        } catch {
            /* ignore — logged out */
        }
    }, []);

    useEffect(() => {
        fetchUnread();
        if (!connected) {
            pollRef.current = setInterval(fetchUnread, 60000);
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [fetchUnread, connected]);

    // Realtime — bump on new message, refetch on read.
    useEffect(() => {
        const offNew = subscribe("message:new", () => setUnread((u) => u + 1));
        const offRead = subscribe("message:read", fetchUnread);
        return () => {
            offNew();
            offRead();
        };
    }, [subscribe, fetchUnread]);

    if (variant === "bottom") {
        return (
            <NavLink
                to="/messages"
                data-testid="mobile-nav-messages"
                className={({ isActive }) =>
                    `relative flex flex-col items-center justify-center gap-1 transition-colors ${
                        isActive ? "text-ember-orange" : "text-slate-gray"
                    }`
                }
            >
                <span className="relative">
                    <MessageCircle className="w-5 h-5" />
                    {unread > 0 && (
                        <span
                            data-testid="mobile-messages-badge"
                            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-ember-orange text-white text-[9px] font-bold leading-4 text-center"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider">Messages</span>
            </NavLink>
        );
    }

    return (
        <NavLink
            to="/messages"
            data-testid="nav-messages"
            className={({ isActive }) =>
                `group relative flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive ? "bg-midnight-pine text-white" : "text-granite hover:bg-trail-tan/50"
                }`
            }
        >
            <span className="relative shrink-0">
                <MessageCircle className="w-5 h-5" />
                {unread > 0 && (
                    <span
                        data-testid="nav-messages-badge"
                        className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ember-orange text-white text-[10px] font-bold leading-[18px] text-center"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </span>
            <span className="font-semibold text-sm">Messages</span>
        </NavLink>
    );
}

function SideNavItem({ to, label, icon: Icon, testId, locked, onLockedClick }) {
    if (locked) {
        return (
            <button
                type="button"
                data-testid={testId}
                onClick={onLockedClick}
                className="group w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-slate-gray hover:bg-trail-tan/30 transition-all duration-200"
            >
                <Icon className="w-5 h-5 shrink-0 opacity-60" />
                <span className="font-semibold text-sm flex-1 opacity-80">{label}</span>
                <Lock className="w-3.5 h-3.5 text-ember-orange shrink-0" />
            </button>
        );
    }
    return (
        <NavLink
            to={to}
            end={to === "/feed"}
            data-testid={testId}
            className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                        ? "bg-midnight-pine text-white"
                        : "text-granite hover:bg-trail-tan/50"
                }`
            }
        >
            <Icon className="w-5 h-5 shrink-0" />
            <span className="font-semibold text-sm">{label}</span>
        </NavLink>
    );
}

const SECTIONS_KEY = "rwo_sidenav_sections";
const DEFAULT_SECTIONS = { main: true, you: true, progress: true, discover: true, admin: true };

function loadSections() {
    try {
        const raw = localStorage.getItem(SECTIONS_KEY);
        return raw ? { ...DEFAULT_SECTIONS, ...JSON.parse(raw) } : { ...DEFAULT_SECTIONS };
    } catch {
        return { ...DEFAULT_SECTIONS };
    }
}

// Route → section, so we can auto-expand the section holding the active page.
const SECTION_BY_PREFIX = [
    ["main", ["/feed", "/explore", "/create", "/marketplace"]],
    ["you", ["/profile", "/notifications", "/messages", "/offline"]],
    ["progress", ["/passport", "/challenges", "/friend-challenges", "/leaderboards"]],
    ["discover", ["/brands", "/pricing"]],
    ["admin", ["/admin"]],
];
function sectionForPath(pathname) {
    for (const [id, prefixes] of SECTION_BY_PREFIX) {
        if (prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"))) return id;
    }
    return null;
}

function NavSection({ id, title, open, onToggle, children }) {
    return (
        <div>
            <button
                type="button"
                data-testid={`navsection-${id}`}
                onClick={onToggle}
                aria-expanded={open}
                className="w-full flex items-center justify-between px-4 pt-5 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-gray/70 hover:text-granite transition-colors"
            >
                <span>{title}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
            </button>
            {open && <div className="space-y-1">{children}</div>}
        </div>
    );
}

export function SideNav() {
    const { user, logout } = useAuth();
    const gate = useTierGate(user);
    const isFree = user && user.role !== "admin" && (user.tier || "free") === "free";
    const lockedClick = (feature) => () =>
        gate({ required_tier: "adventure", feature });

    const [sections, setSections] = useState(loadSections);
    const toggleSection = (sid) =>
        setSections((s) => {
            const next = { ...s, [sid]: !s[sid] };
            try { localStorage.setItem(SECTIONS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });

    // Auto-expand the section containing the current page (in-memory only, so
    // it never hides your active route — without overwriting saved preferences).
    const { pathname } = useLocation();
    useEffect(() => {
        const sid = sectionForPath(pathname);
        if (sid) setSections((s) => (s[sid] ? s : { ...s, [sid]: true }));
    }, [pathname]);

    return (
        <aside
            data-testid="sidenav"
            className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-ridge-line bg-snowcap z-30"
        >
            <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 px-6 py-6 border-b border-ridge-line">
                <img
                    src={BRAND.logoUrl}
                    alt={BRAND.name}
                    className="w-10 h-10 object-contain shrink-0"
                />
                <div>
                    <div className="font-display font-black text-lg text-midnight-pine leading-none tracking-tight">
                        {BRAND.nameWordOne}
                    </div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-gray">
                        {BRAND.nameWordTwo}
                    </div>
                </div>
            </Link>

            <nav className="flex-1 px-3 py-4 overflow-y-auto">
                <NavSection id="main" title="Main" open={sections.main} onToggle={() => toggleSection("main")}>
                    <SideNavItem to="/feed" label="Feed" icon={Home} testId="nav-feed" />
                    <SideNavItem to="/explore" label="Explore" icon={Compass} testId="nav-explore" />
                    <SideNavItem
                        to="/create"
                        label="Create"
                        icon={PlusCircle}
                        testId="nav-create"
                        locked={isFree}
                        onLockedClick={lockedClick("post adventures")}
                    />
                    <SideNavItem to="/marketplace" label="Marketplace" icon={Store} testId="nav-marketplace" />
                </NavSection>

                <NavSection id="you" title="You" open={sections.you} onToggle={() => toggleSection("you")}>
                    <SideNavItem to="/profile" label="Profile" icon={User} testId="nav-profile" />
                    <NotificationBell variant="sidebar" />
                    <MessagesNavLink variant="sidebar" />
                    <SideNavItem to="/offline" label="Offline Guides" icon={CloudOff} testId="nav-offline" />
                </NavSection>

                <NavSection id="progress" title="Progress" open={sections.progress} onToggle={() => toggleSection("progress")}>
                    <SideNavItem
                        to="/passport"
                        label="Passport"
                        icon={Stamp}
                        testId="nav-passport"
                        locked={isFree}
                        onLockedClick={lockedClick("view the Adventure Passport")}
                    />
                    <SideNavItem
                        to="/challenges"
                        label="Challenges"
                        icon={Trophy}
                        testId="nav-challenges"
                        locked={isFree}
                        onLockedClick={lockedClick("take on challenges")}
                    />
                    <SideNavItem
                        to="/friend-challenges"
                        label="Friend Challenges"
                        icon={Users}
                        testId="nav-friend-challenges"
                        locked={isFree}
                        onLockedClick={lockedClick("join friend challenges")}
                    />
                    <SideNavItem to="/leaderboards" label="Leaderboards" icon={Medal} testId="nav-leaderboards" />
                </NavSection>

                <NavSection id="discover" title="Discover" open={sections.discover} onToggle={() => toggleSection("discover")}>
                    <SideNavItem to="/brands" label="Brands" icon={BadgeCheck} testId="nav-brands" />
                    <SideNavItem to="/pricing" label="Pricing" icon={Crown} testId="nav-pricing" />
                </NavSection>

                {user?.role === "admin" && (
                    <NavSection id="admin" title="Admin" open={sections.admin} onToggle={() => toggleSection("admin")}>
                        <SideNavItem to="/admin" label="Admin" icon={Shield} testId="nav-admin" />
                    </NavSection>
                )}
            </nav>

            <div className="p-4 border-t border-ridge-line">
                <Link
                    to="/profile"
                    data-testid="sidenav-user-block"
                    className="flex items-center gap-3 mb-3 px-2 py-1.5 -mx-2 rounded-md hover:bg-alpine-mist transition-colors"
                >
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-trail-tan flex items-center justify-center">
                        {user?.avatar ? (
                            <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                            <User className="w-5 h-5 text-midnight-pine" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-granite truncate">{user?.name}</div>
                        <div className="text-xs text-slate-gray truncate capitalize">{user?.tier} member</div>
                    </div>
                </Link>
                <div className="flex items-center gap-2">
                    <Link
                        to="/settings"
                        data-testid="sidenav-settings-btn"
                        title="Settings"
                        className="flex items-center justify-center w-10 h-10 rounded-md border border-ridge-line text-slate-gray hover:bg-alpine-mist hover:text-granite active:scale-95 transition-all shrink-0"
                    >
                        <Settings className="w-4 h-4" />
                    </Link>
                    <button
                        data-testid="sidenav-logout-btn"
                        onClick={async () => {
                            await logout();
                            window.location.assign("/");
                        }}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-ridge-line text-slate-gray text-sm font-semibold hover:bg-alpine-mist active:scale-95 transition-all"
                    >
                        <LogOut className="w-4 h-4" /> Log out
                    </button>
                </div>
            </div>
        </aside>
    );
}

export function BottomNav() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const gate = useTierGate(user);
    const isFree = user && user.role !== "admin" && (user.tier || "free") === "free";
    const { setOpen } = useMobileMenu();

    return (
        <>
        <nav
            data-testid="bottomnav"
            className="lg:hidden fixed bottom-0 inset-x-0 h-16 bg-snowcap border-t border-ridge-line z-30 grid grid-cols-6 pb-safe"
        >
            {NAV.map((n) => {
                const active = pathname === n.to || (n.to === "/profile" && pathname.startsWith("/profile"));
                const Icon = n.icon;
                const locked = isFree && n.to === "/create";
                if (locked) {
                    return (
                        <button
                            key={n.to}
                            type="button"
                            data-testid={`mobile-${n.testId}`}
                            onClick={() => gate({ required_tier: "adventure", feature: "post adventures" })}
                            className="flex flex-col items-center justify-center gap-1 text-slate-gray relative"
                        >
                            <span className="relative">
                                <Icon className="w-5 h-5 opacity-60" />
                                <Lock className="w-2.5 h-2.5 text-ember-orange absolute -top-0.5 -right-1" />
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">{n.label}</span>
                        </button>
                    );
                }
                return (
                    <Link
                        key={n.to}
                        to={n.to}
                        data-testid={`mobile-${n.testId}`}
                        className={`flex flex-col items-center justify-center gap-1 transition-colors ${
                            active ? "text-ember-orange" : "text-slate-gray"
                        }`}
                    >
                        <Icon className="w-5 h-5" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">{n.label}</span>
                    </Link>
                );
            })}
            <button
                type="button"
                data-testid="mobile-nav-menu"
                onClick={() => setOpen(true)}
                className="flex flex-col items-center justify-center gap-1 text-slate-gray transition-colors active:text-ember-orange"
            >
                <MenuIcon className="w-5 h-5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Menu</span>
            </button>
        </nav>
        <MobileMenu />
        </>
    );
}

function MobileMenu() {
    const { open, setOpen } = useMobileMenu();
    const onClose = () => setOpen(false);
    const { user, logout } = useAuth();
    const { pathname } = useLocation();
    const gate = useTierGate(user);
    const isFree = user && user.role !== "admin" && (user.tier || "free") === "free";
    const lockedClick = (feature) => () => {
        onClose();
        gate({ required_tier: "adventure", feature });
    };

    // Close on navigation + lock body scroll while open.
    useEffect(() => { onClose(); /* eslint-disable-next-line */ }, [pathname]);
    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    if (!open) return null;

    return (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-menu">
            <div
                className="absolute inset-0 bg-midnight-pine/50 backdrop-blur-sm"
                onClick={onClose}
                data-testid="mobile-menu-backdrop"
            />
            <div className="absolute inset-y-0 right-0 w-[86%] max-w-sm bg-snowcap flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
                <div className="flex items-center justify-between px-5 py-4 border-b border-ridge-line">
                    <div className="flex items-baseline gap-1.5">
                        <span className="font-display font-black text-lg text-midnight-pine">{BRAND.nameWordOne}</span>
                        <span className="font-display font-black text-lg text-ember-orange">{BRAND.nameWordTwo}</span>
                    </div>
                    <button
                        data-testid="mobile-menu-close"
                        onClick={onClose}
                        className="w-9 h-9 flex items-center justify-center rounded-full text-slate-gray hover:bg-alpine-mist"
                        aria-label="Close menu"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
                    <SideNavItem to="/feed" label="Feed" icon={Home} testId="m-nav-feed" />
                    <SideNavItem to="/explore" label="Explore" icon={Compass} testId="m-nav-explore" />
                    <SideNavItem to="/create" label="Create" icon={PlusCircle} testId="m-nav-create" locked={isFree} onLockedClick={lockedClick("post adventures")} />
                    <SideNavItem to="/marketplace" label="Marketplace" icon={Store} testId="m-nav-marketplace" />
                    <SideNavItem to="/profile" label="Profile" icon={User} testId="m-nav-profile" />
                    <div className="my-2 border-t border-ridge-line" />
                    <NotificationBell variant="sidebar" />
                    <MessagesNavLink variant="sidebar" />
                    <SideNavItem to="/offline" label="Offline Guides" icon={CloudOff} testId="m-nav-offline" />
                    <SideNavItem to="/passport" label="Passport" icon={Stamp} testId="m-nav-passport" locked={isFree} onLockedClick={lockedClick("view the Adventure Passport")} />
                    <SideNavItem to="/challenges" label="Challenges" icon={Trophy} testId="m-nav-challenges" locked={isFree} onLockedClick={lockedClick("take on challenges")} />
                    <SideNavItem to="/friend-challenges" label="Friend Challenges" icon={Users} testId="m-nav-friend-challenges" locked={isFree} onLockedClick={lockedClick("join friend challenges")} />
                    <SideNavItem to="/leaderboards" label="Leaderboards" icon={Medal} testId="m-nav-leaderboards" />
                    <SideNavItem to="/brands" label="Brands" icon={BadgeCheck} testId="m-nav-brands" />
                    <SideNavItem to="/pricing" label="Pricing" icon={Crown} testId="m-nav-pricing" />
                    {user?.role === "admin" && (
                        <SideNavItem to="/admin" label="Admin" icon={Shield} testId="m-nav-admin" />
                    )}
                </nav>

                <div className="p-4 border-t border-ridge-line">
                    <Link
                        to="/profile"
                        data-testid="mobile-menu-user-block"
                        className="flex items-center gap-3 mb-3 px-2 py-1.5 -mx-2 rounded-md hover:bg-alpine-mist transition-colors"
                    >
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-trail-tan flex items-center justify-center shrink-0">
                            {user?.avatar ? (
                                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                                <User className="w-5 h-5 text-midnight-pine" />
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-bold text-granite truncate">{user?.name}</div>
                            <div className="text-xs text-slate-gray truncate capitalize">{user?.tier} member</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/settings"
                            data-testid="mobile-menu-settings-btn"
                            className="flex items-center justify-center gap-2 flex-1 px-3 py-2.5 rounded-md border border-ridge-line text-slate-gray text-sm font-semibold hover:bg-alpine-mist active:scale-95 transition-all"
                        >
                            <Settings className="w-4 h-4" /> Settings
                        </Link>
                        <button
                            data-testid="mobile-menu-logout-btn"
                            onClick={async () => {
                                onClose();
                                await logout();
                                window.location.assign("/");
                            }}
                            className="flex items-center justify-center gap-2 flex-1 px-3 py-2.5 rounded-md bg-midnight-pine text-white text-sm font-bold hover:bg-deep-evergreen active:scale-95 transition-all"
                        >
                            <LogOut className="w-4 h-4" /> Log out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function TopBar({ title, subtitle, right }) {
    const { user } = useAuth();
    const { setOpen } = useMobileMenu();
    return (
        <header className="lg:hidden sticky top-0 z-20 bg-snowcap border-b border-ridge-line px-4 py-3 flex items-center justify-between">
            <div className="min-w-0">
                <h1 className="font-display font-black text-xl text-midnight-pine leading-none truncate">
                    {title}
                </h1>
                {subtitle && <p className="text-xs text-slate-gray mt-0.5 truncate">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {right}
                <button
                    type="button"
                    data-testid="topbar-avatar-menu"
                    onClick={() => setOpen(true)}
                    aria-label="Open menu"
                    className="w-9 h-9 rounded-full overflow-hidden bg-trail-tan flex items-center justify-center ring-1 ring-ridge-line active:scale-95 transition-transform"
                >
                    {user?.avatar ? (
                        <img src={user.avatar} alt={user.name || "Profile"} className="w-full h-full object-cover" />
                    ) : (
                        <User className="w-4 h-4 text-midnight-pine" />
                    )}
                </button>
            </div>
        </header>
    );
}
